import React, { useEffect, useState } from "react";
import { Input, Button, Modal, Select, Radio, Alert, message } from "antd";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import ButtonLoader from "../ButtonLoader";

const { Option } = Select;

const schema = yup.object().shape({
  name: yup.string().required("Schedule name is required"),
  screen: yup
    .array()
    .min(1, "At least one screen is required")
    .required("Screen is required"),
  status: yup.string().required("Status is required"),
});

const ScheduleForm = ({
  isModalOpen,
  editingSchedule,
  closeModal,
  handleUpdateList,
}) => {
  const [loading, setLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDataSet, setIsDataSet] = useState(false);
  const [screenOptions, setScreenOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit: !!editingSchedule },
    defaultValues: {
      status: "active",
    },
  });

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/schedules/masters`
      );

      const status = response.data.data.status;
      const screens = response.data.data.screens;

      setStatusOptions(status);
      setScreenOptions(
        screens.map((screen) => {
          return {
            value: screen.id,
            label: screen.name,
          };
        })
      );
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedule = async (scheduleId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/schedules/${scheduleId}`
      );
      const selectedScreen = screenOptions.find((screen) =>
        response.data.data.screen_ids.includes(screen.value)
      );

      //
      const editFormData = {
        ...response.data.data,
        screen: selectedScreen && response.data.data.screen_ids,
      };

      reset(editFormData);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDataAndSetState = async () => {
      await fetchMasterData();
      setIsDataSet(true);
    };
    fetchDataAndSetState();
  }, []);

  useEffect(() => {
    if (isDataSet && editingSchedule) {
      fetchSchedule(editingSchedule.id);
    }
  }, [isDataSet, editingSchedule]);

  const onSubmitSchedule = async (data) => {
    let differFormFields = {};
    if (editingSchedule !== null) {
      differFormFields = {
        id: editingSchedule.id,
      };
    }
    const finalFormData = {
      name: data.name.trim(),
      screen_ids: data.screen,
      status: data.status,
      ...differFormFields,
    };
    setSubmitting(true);
    let newFormData = finalFormData;
    const url =
      editingSchedule === null
        ? `${process.env.REACT_APP_BASE_URL}/api/schedules/create`
        : `${process.env.REACT_APP_BASE_URL}/api/schedules/update`;

    try {
      if (editingSchedule === null) {
        await axios.post(url, newFormData);
        // Reset form after successful submission
        // reset(newFormData);
        message.success("Schedule created successfully");
      } else {
        const response = await axios.put(url, newFormData);
        const updated_row_count = response.data.data.updated_row_count;
        if (updated_row_count === 1) {
          // reset(data);
          message.success("Schedule updated successfully");
        }
      }
      setSubmitting(false);
      setShowErrors(false);
      handleUpdateList();
      closeModal();
    } catch (error) {
      if (error.response && error.response.status > 300) {
        const errorData = error.response.data.message;
        // const errorField = error.response.data.field;
        console.log(errorData);
        // setShowErrors(false);
        setErrorsMessage(errorData);
        setShowErrors(true);
        setTimeout(() => {
          setShowErrors(false);
        }, 3000);

        setSubmitting(false);
      } else {
        console.error("Error:", error);
      }
    }
  };

  return (
    <Modal
      title={editingSchedule ? "Edit Schedule" : "Add New Schedule"}
      open={isModalOpen}
      onCancel={() => {
        reset();
        // setEditingSchedule(null);
        closeModal();
      }}
      footer={null}
      maskClosable={false}
    >
      {loading ? (
        <ButtonLoader wrapperClass={`min-h-50`} />
      ) : (
        <>
          {showErrors && (
            <>
              <div className="col-sm-12 mb-3">
                <Alert message={errorsMessage} type="error" closeIcon={false} />
              </div>
            </>
          )}
          <form onSubmit={handleSubmit(onSubmitSchedule)}>
            <div className="mb-3">
              <label className="form-label">
                Schedule Name <span className="text-danger">*</span>
              </label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.name && (
                <div className="text-danger small">{errors.name.message}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">
                Screens <span className="text-danger">*</span>
              </label>
              <Controller
                name="screen"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    mode="multiple"
                    showSearch
                    filterOption={(input, option) => {
                      return (option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }}
                    style={{ width: "100%" }}
                    onChange={field.onChange}
                  >
                    {screenOptions.map((screen) => (
                      <Option value={screen.value} key={screen.value}>
                        {screen.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.screen && (
                <div className="text-danger small">{errors.screen.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label me-3">Status</label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Radio.Group {...field}>
                    {statusOptions.map((status) => (
                      <Radio value={status.value} key={status.value}>
                        {status.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                )}
              />
              {errors.status && (
                <div className="text-danger small">{errors.status.message}</div>
              )}
            </div>

            <Button
              type="primary"
              htmlType="submit"
              block
              disabled={submitting}
            >
              {!submitting ? (
                editingSchedule ? (
                  "Update Schedule"
                ) : (
                  "Add Schedule"
                )
              ) : (
                <ButtonLoader text="Submitting..." />
              )}
            </Button>
          </form>
        </>
      )}
    </Modal>
  );
};

export default ScheduleForm;
